import {
  pushContactUsErrorEvent,
  pushContactUsEvent,
  pushViewItemEvent,
} from './dataLayer';

/**
 * @param {HTMLSelectElement|HTMLInputElement|null} el
 * @returns {string}
 */
const readFieldDisplayValue = (el) => {
  if (!el) {
    return '';
  }

  if (el instanceof HTMLSelectElement) {
    const option = el.selectedOptions?.[0];
    return (option?.textContent || el.value || '').trim();
  }

  return (el.value || '').trim();
};

const getCheckedFieldValue = (field) => {
  const checked = Array.from(field.form?.elements || []).find(
    (candidate) => candidate.name === field.name && candidate.checked,
  );
  const label = checked?.closest('label');

  return (label?.textContent || checked?.value || '').trim();
};

const readContactFieldValue = (field) => {
  if (!field) {
    return '';
  }

  if (field.type === 'radio' || field.type === 'checkbox') {
    return getCheckedFieldValue(field);
  }

  return readFieldDisplayValue(field);
};

const findFieldByKeywords = (form, keywords) => {
  const fields = Array.from(form.querySelectorAll('input, select, textarea'));

  return fields.find((field) => {
    const haystack = [
      field.name,
      field.id,
      field.className,
      field.getAttribute('aria-label'),
      field.closest('label')?.textContent,
    ].join(' ').toLowerCase();

    return keywords.some((keyword) => haystack.includes(keyword));
  }) || null;
};

const getContactUsFields = (form) => {
  const countryEl = form.querySelector('[data-azds-contact-field="country"]')
    || findFieldByKeywords(form, ['country']);
  const experienceEl = form.querySelector('[data-azds-contact-field="experience_interest"]')
    || findFieldByKeywords(form, ['experience interest', 'experience_interest', 'experience-interest', 'interest_in_experiences']);
  const subjectEl = form.querySelector('[data-azds-contact-field="subject"]')
    || findFieldByKeywords(form, ['subject', 'topic']);

  return {
    country: readContactFieldValue(countryEl),
    experienceInterest: readContactFieldValue(experienceEl),
    subject: readContactFieldValue(subjectEl),
  };
};

const isContactUsForm = (form) => form?.matches?.(
  '.wpcf7-form--contact-us, form[data-azds-form="contact-us"]',
);

const getContactUsErrorMessage = (event, form) => {
  const responseMessage = event.detail?.apiResponse?.message;
  if (responseMessage) {
    return responseMessage;
  }

  return form.querySelector('.wpcf7-response-output')?.textContent.trim() || '';
};

/**
 * Contact Us form: wire when markup uses data-azds-form="contact-us" and
 * data-azds-contact-field="country|experience_interest|subject" on inputs/selects.
 * For AJAX forms, call pushContactUsEvent from application code on success instead.
 */
const initContactUsFormAnalytics = () => {
  const form = document.querySelector('form[data-azds-form="contact-us"]');
  if (!form) {
    return;
  }

  form.addEventListener('submit', () => {
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
      return;
    }

    const countryEl = form.querySelector('[data-azds-contact-field="country"]');
    const experienceEl = form.querySelector('[data-azds-contact-field="experience_interest"]');
    const subjectEl = form.querySelector('[data-azds-contact-field="subject"]');

    const field1 = readFieldDisplayValue(countryEl);
    const field2 = readFieldDisplayValue(experienceEl);
    const field3 = readFieldDisplayValue(subjectEl);

    if (!field1 && !field2 && !field3) {
      return;
    }

    pushContactUsEvent({
      country: field1,
      experienceInterest: field2,
      subject: field3,
    });
  });
};

const initContactUsCf7Analytics = () => {
  document.addEventListener('wpcf7mailsent', (event) => {
    const form = event.target;
    if (!isContactUsForm(form)) {
      return;
    }

    pushContactUsEvent(getContactUsFields(form));
  });

  ['wpcf7invalid', 'wpcf7mailfailed', 'wpcf7spam'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      const form = event.target;
      if (!isContactUsForm(form)) {
        return;
      }

      pushContactUsErrorEvent({
        ...getContactUsFields(form),
        errorMessage: getContactUsErrorMessage(event, form),
      });
    });
  });
};

/**
 * Optional: experience view_item when page includes
 * <script type="application/json" id="azds-analytics-view-item">{ ...ecommerce object... }</script>
 */
const initViewItemFromEmbeddedJson = () => {
  const script = document.getElementById('azds-analytics-view-item');
  if (!script || !script.textContent) {
    return;
  }

  try {
    const ecommerce = JSON.parse(script.textContent);
    pushViewItemEvent(ecommerce);
  } catch (error) {
    // Invalid JSON — skip silently in production
  }
};

/**
 * Dashboard / experience analytics hooks from implementation strategy PDFs (1)–(7).
 */
export default function initDashboardAnalytics() {
  if (typeof document === 'undefined') {
    return;
  }

  const run = () => {
    initContactUsFormAnalytics();
    initContactUsCf7Analytics();
    initViewItemFromEmbeddedJson();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
}
